import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Link } from '@tanstack/react-router';
import { useRequest } from 'ahooks';
import { Flex, Form, Input, message } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { sendVerificationCode, signUp } from '@/apis/user.api.ts';

import AuthFormLayout from '../components/AuthFormLayout';
import VerificationCodeInput from '../components/VerificationCodeInput';
import { useFormValidation } from '../hooks/useFormValidation';
import type { RegisterFormData } from '../models';

export const Route = createFileRoute({
  component: SignUpPage,
});

function SignUpPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const { getEmailRules, getPasswordRules, validatePasswordMatch } =
    useFormValidation();

  const { run: runSendCode, loading: sendingCode } = useRequest(
    sendVerificationCode,
    {
      manual: true,
    },
  );

  const { run: runRegister, loading: registering } = useRequest(signUp, {
    manual: true,
    onSuccess: () => {
      message.success(t('auth.message.registerSuccess'));
    },
    onError: (error) => {
      message.error(t('auth.message.registerFailed') + error.message);
    },
  });

  const handleSendCode = async (email: string) => {
    await runSendCode(email);
  };

  const onFinish = (values: RegisterFormData) => {
    if (!validatePasswordMatch(values.password, values.confirmPassword)) {
      return;
    }
    runRegister({ ...values, email: values.account });
  };

  return (
    <AuthFormLayout<RegisterFormData>
      title={t('auth.register.title')}
      subtitle={t('auth.register.subtitle')}
      submitText={t('auth.register.submit')}
      loading={registering}
      onFinish={onFinish}
    >
      <Form.Item
        name="account"
        rules={getEmailRules()}
        style={{ marginBottom: 16 }}
      >
        <Input
          size="large"
          prefix={<MailOutlined />}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.form.email.placeholder')}
        />
      </Form.Item>

      <VerificationCodeInput
        email={email}
        onSendCode={handleSendCode}
        loading={sendingCode}
      />

      <Form.Item
        name="password"
        rules={getPasswordRules(6)}
        style={{ marginBottom: 16 }}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined />}
          placeholder={t('auth.form.password.placeholder')}
        />
      </Form.Item>

      <Form.Item
        name="confirmPassword"
        rules={[
          {
            required: true,
            message: t('auth.validation.confirmPassword.required'),
          },
        ]}
        style={{ marginBottom: 24 }}
      >
        <Input.Password
          size="large"
          prefix={<LockOutlined />}
          placeholder={t('auth.form.confirmPassword.placeholder')}
        />
      </Form.Item>

      <Flex
        vertical
        gap="small"
        style={{
          width: '100%',
          textAlign: 'center',
          marginTop: 8,
        }}
      >
        <Link
          from="/"
          to="/auth/sign-in"
          className="text-blue-600 transition-colors hover:text-blue-800"
        >
          {t('auth.links.signIn')}
        </Link>
        <Link
          from="/"
          to="/auth/reset-password"
          className="text-blue-600 transition-colors hover:text-blue-800"
        >
          {t('auth.links.forgotPasswordShort')}
        </Link>
      </Flex>
    </AuthFormLayout>
  );
}
